import { useParams, useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronLeft, MapPin, Loader2, Trash2 } from "lucide-react";
import {
  getLead, crmMeta, patchLead, deleteLead, addNote, addContact, editContact, deleteContact, setMainContact, addQuote, deleteQuote, fmtDate,
} from "@/lib/crmApi";
import { useAuth } from "@/contexts/AuthContext";
import { PickUpBox } from "@/components/crm/PickUpBox";
import { ProductPicker } from "@/components/crm/ProductPicker";
import { ContactsSection } from "@/components/crm/ContactsSection";
import { QuotesSection } from "@/components/crm/QuotesSection";
import { ActivityLog } from "@/components/crm/ActivityLog";
import { CountrySelect } from "@/components/crm/CountrySelect";

const useLeadAction = (id, fn, okMsg) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ args }) => fn(id, ...args),
    onSuccess: (lead, { done }) => {
      qc.setQueryData(["crm-lead", id], lead);
      qc.invalidateQueries({ queryKey: ["crm-leads"] });
      if (okMsg) toast.success(okMsg);
      done?.();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't save"),
  });
};

export default function CRMLead() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { data: lead, isLoading } = useQuery({ queryKey: ["crm-lead", id], queryFn: () => getLead(id) });
  const { data: meta } = useQuery({ queryKey: ["crm-meta"], queryFn: crmMeta, staleTime: Infinity });

  const patch = useLeadAction(id, patchLead, "Saved");
  const note = useLeadAction(id, addNote, "Note saved");
  const cAdd = useLeadAction(id, addContact, "Contact added");
  const cEdit = useLeadAction(id, editContact, "Contact updated");
  const cDel = useLeadAction(id, deleteContact, "Contact removed");
  const cMain = useLeadAction(id, setMainContact, "Main contact changed");
  const qAdd = useLeadAction(id, addQuote, "Quote saved");
  const qDel = useLeadAction(id, deleteQuote, "Quote removed");
  const del = useMutation({
    mutationFn: () => deleteLead(id),
    onSuccess: () => { toast.success("Lead deleted"); nav("/crm"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't delete"),
  });

  if (isLoading || !lead || !meta) return <div className="py-24 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-slate-300" /></div>;

  return (
    <div data-testid="crm-lead-page" className="max-w-4xl mx-auto space-y-5">
      <Link to="/crm" data-testid="crm-back" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ChevronLeft className="h-4 w-4" /> Leads</Link>

      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 data-testid="lead-customer-name" className="text-3xl font-semibold tracking-tight text-slate-900">{lead.customer_name}</h1>
          <div className="flex items-center gap-1 mt-1 text-slate-500">
            <MapPin className="h-4 w-4" />
            <CountrySelect testId="lead-country" value={lead.country} onChange={(v) => patch.mutate({ args: [{ country: v }] })}
              className="h-9 border-transparent hover:border-slate-300 bg-transparent px-1 text-slate-600" />
          </div>
          <p className="text-xs text-slate-400 mt-2">Created {fmtDate(lead.created_at)} by {lead.created_by} · Updated {fmtDate(lead.updated_at)} by {lead.updated_by}</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="lead-stage" value={lead.stage} onChange={(e) => patch.mutate({ args: [{ stage: e.target.value }] })}
            className="h-12 pl-4 pr-8 rounded-xl border border-slate-300 bg-white text-sm font-semibold">
            {meta.stages.map((s) => <option key={s}>{s}</option>)}
          </select>
          {user?.role === "director" && (
            <button data-testid="lead-delete" title="Delete lead" onClick={() => window.confirm(`Delete the lead for ${lead.customer_name}? This removes its contacts, quotes and history.`) && del.mutate()}
              className="h-12 w-12 rounded-xl border border-slate-200 text-slate-400 hover:text-rose-600 hover:border-rose-200 flex items-center justify-center transition-colors">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </header>

      <PickUpBox note={lead.last_note} saving={note.isPending} onSave={(text, done) => note.mutate({ args: [text], done })} />
      <ProductPicker meta={meta} lead={lead} onPatch={(body) => patch.mutate({ args: [body] })} />
      <ContactsSection lead={lead} saving={cAdd.isPending || cEdit.isPending}
        onAdd={(c, done) => cAdd.mutate({ args: [c], done })}
        onEdit={(cid, c, done) => cEdit.mutate({ args: [cid, c], done })}
        onDelete={(cid) => cDel.mutate({ args: [cid] })}
        onMain={(cid) => cMain.mutate({ args: [cid] })} />
      <QuotesSection lead={lead} meta={meta} saving={qAdd.isPending}
        onAdd={(q, done) => qAdd.mutate({ args: [q], done })}
        onDelete={(qid) => qDel.mutate({ args: [qid] })} />
      <ActivityLog items={lead.activities} />
    </div>
  );
}
